import { Box, DialogActions, DialogContent } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const TransferOwnershipWrapper = styled(Box)`
  .content {
    padding: ${theme.spacing.xSmall}px ${theme.spacing.small}px;
    margin-bottom: ${theme.spacing.xLarge}px;

    .title {
      font-size: 36px;
      font-family: ${theme.typography.fontFamily.primary};
      line-height: 44px;
      color: ${theme.colors.menuItemText};
    }

    .text {
      color: ${theme.colors.menuItemText};
      font-size: ${theme.typography.fontSize.large};
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 20px;
      letter-spacing: 0.1px;
      margin-top: ${theme.spacing.xSmall}px;
    }

    .MuiFormControl-root {
      margin-top: ${theme.spacing.small}px;

      .MuiInputLabel-root {
        color: ${theme.colors.menuItemText};
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.xLarge};
        line-height: 24px;
        letter-spacing: 0.5px;
      }

      .MuiOutlinedInput-root {
        color: ${theme.colors.menuItemText};
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.xLarge};
        line-height: 24px;
        letter-spacing: 0.5px;

        &:hover {
          border: transparent;
        }

        fieldset {
          border: 1px solid ${theme.colors.controlBorder};
          border-radius: ${theme.attributes.borderRadius.small};
        }
      }

      .MuiFormHelperText-root.Mui-error {
        color: ${theme.colors.errorLight};
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.medium};
        margin-left: 0;
        line-height: 24px;
        letter-spacing: 0.5px;
      }
    }
  }
`

export const Header = styled(Box)`
  padding: ${theme.spacing.small}px;
  border-bottom: 1px solid ${theme.colors.analyzerBorder};
  display: flex;
  justify-content: space-between;
  align-items: center;

  .title {
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.normal};
    font-size: 36px;
    line-height: 44px;
    color: ${theme.colors.menuItemText};
  }

  html[data-theme='light'] & svg path {
    fill: ${theme.colors.menuItemText};
  }
`

export const Content = styled(DialogContent)<{ userrole: string }>`
  padding: ${theme.spacing.medium}px ${theme.spacing.xLarge}px;
  max-width: 700px;
  margin: 0 auto;

  .info-wrapper {
    display: flex;
    flex-flow: column wrap;
    justify-content: center;
    align-items: flex-start;

    &:not(:last-of-type) {
      border-bottom: 1px solid ${theme.colors.analyzerBorder};
    }

    .info-item {
      display: flex;
      flex-flow: column wrap;
      justify-content: center;
      align-items: flex-start;
      width: 100%;
      margin: ${theme.spacing.lg}px 0;

      &:first-of-type {
        margin-top: 0;
      }

      .title {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.xLarge};
        line-height: 24px;
        letter-spacing: 0.1px;
        color: ${theme.colors.devicesSecondaryText};
        margin-right: ${theme.spacing.medium}px;
        margin-bottom: ${theme.spacing.half}px;
      }

      .description-box {
        display: flex;
        align-items: center;
        width: 100%;

        .permissions-container {
          min-width: 100px;
        }

        button {
          white-space: nowrap;
        }

        .permissions-title {
          width: fit-content;
          min-width: 64px;
          height: 32px;
          border-radius: ${theme.attributes.borderRadius.mediumSmall};
          display: flex;
          justify-content: center;
          align-items: center;
          padding: ${theme.spacing.xsm}px ${theme.spacing.md}px;
          gap: ${theme.spacing.xSmall}px;
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.large};
          line-height: 20px;
          letter-spacing: 0.1px;
          text-transform: capitalize;

          ${({ userrole }) =>
            userrole === 'owner' &&
            `
              background: ${theme.colors.textPrimary};
              opacity: 0.9;
              color: ${theme.baseColors.primary[20]};
          `};

          ${({ userrole }) =>
            userrole === 'admin' &&
            `
              background: #B2EBF2;
              color: ${theme.colors.primaryDark};
          `};

          ${({ userrole }) =>
            ['cpi manager', 'manager', 'cpi_manager'].includes(userrole) &&
            `
              background: ${theme.baseColors.info[20]};
              color: ${theme.colors.primaryDark};
          `};

          ${({ userrole }) =>
            userrole === 'viewer' &&
            `
              background: ${theme.baseColors.info[30]};
              color: ${theme.colors.primaryDark};
          `};

          ${({ userrole }) =>
            userrole === 'default' &&
            `
              background: ${theme.baseColors.info[10]};
              color: ${theme.colors.white};
          `};

          ${({ userrole }) =>
            userrole === 'demo' &&
            `
              background: #DEBDD9;
              color: #3F293E;
          `};

          /* Several role colours resolve to near-white in light, which vanishes on the pale chips. */
          html[data-theme='light'] & {
            color: ${theme.colors.menuItemText};

            ${({ userrole }) =>
              userrole === 'owner' &&
              `
                background: ${theme.colors.cta};
                opacity: 1;
                color: #ffffff;
            `};
          }
        }

        .status-container {
        }

        .user-status {
          width: fit-content;
          height: 32px;
          padding: ${theme.spacing.xsm}px 16px;
          gap: ${theme.spacing.xSmall}px;
          border: 1px solid ${theme.colors.controlBorder};
          border-radius: ${theme.attributes.borderRadius.secondary};
          background: transparent;
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.large};
          line-height: 20px;
          display: flex;
          justify-content: center;
          align-items: center;
          text-align: center;
          letter-spacing: 0.1px;
          color: ${theme.colors.menuItemText};
        }

        .description {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.large};
          line-height: 20px;
          letter-spacing: 0.25px;
          color: ${theme.colors.menuItemText};
          margin-right: ${theme.spacing.medium}px;
          word-break: break-all;
        }

        .MuiButtonBase-root {
          border: 1px solid transparent !important;
          width: fit-content;
          padding: ${theme.spacing.xsm}px ${theme.spacing.md}px;

          &:focus,
          &:active,
          &:focus-within,
          &:focus-visible {
            border: 1px solid transparent !important;
            background: transparent !important;
          }
        }
      }
    }
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;

    .MuiFormControl-root {
      width: 250px;

      label {
        color: ${theme.colors.devicesSecondaryText};

        .MuiInputLabel-asterisk {
          color: #fe455d;
        }
      }

      .MuiOutlinedInput-root {
        border-radius: ${theme.attributes.borderRadius.small};
        color: ${theme.colors.devicesSecondaryText};
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.xLarge};
        line-height: 24px;
        letter-spacing: 0.5px;

        fieldset {
          border: 1px solid ${theme.colors.controlBorder} !important;
        }
      }
    }

    .MuiFormHelperText-root {
      margin-left: 0;
      color: ${theme.colors.warningLight};
    }
  }

  .error-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;

    .MuiTypography-root {
      width: 250px;
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.normal};
      font-size: ${theme.typography.fontSize.medium};
      line-height: 16px;
      letter-spacing: 0.4px;
      color: #ffb4ab;
    }
  }

  .roles-wrapper {
    padding-bottom: 54px;

    .roles-title {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.normal};
      font-size: ${theme.typography.fontSize.xxLarge};
      line-height: 28px;
      color: ${theme.colors.menuItemText};

      span {
        color: #fe455d;
      }
    }

    .MuiFormGroup-root {
      .MuiFormControlLabel-root {
        display: flex;
        align-items: flex-start;
        margin-left: ${theme.spacing.xSmall}px;

        &:not(:last-of-type) {
          margin-bottom: ${theme.spacing.small}px;
        }

        &:first-of-type {
          margin-top: ${theme.spacing.xSmall}px;
        }

        .MuiRadio-root {
          padding: 2px 0 0;

          svg {
            width: 20px;
            height: 20px;

            path {
              fill: ${theme.colors.devicesSecondaryText};
            }
          }
        }

        .roles-name {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.normal};
          font-size: ${theme.typography.fontSize.xLarge};
          line-height: 24px;
          letter-spacing: 0.5px;
          color: ${theme.colors.menuItemText};
          margin-left: 18px;
        }

        .roles-description {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.normal};
          font-size: ${theme.typography.fontSize.medium};
          line-height: 16px;
          letter-spacing: 0.4px;
          color: ${theme.colors.devicesSecondaryText};
          margin-left: 18px;
        }
      }
    }
  }
`

export const Actions = styled(DialogActions)`
  background: ${theme.colors.backgroundDarker};
  box-shadow: ${theme.attributes.boxShadow.toolbar};
  border-radius: 0 0 ${theme.attributes.borderRadius.quaternary} ${theme.attributes.borderRadius.quaternary};
  padding: ${theme.spacing.small}px 36px;
  border-top: 1px solid ${theme.colors.analyzerBorder};
`
